export const APP_ROLE_NAMES = ['Owner', 'Manager', 'Technician', 'Commission agent', 'Office', 'Read-only'];

export const ROLE_PERMISSIONS = {
  Owner: ['*'],
  Manager: ['schedule', 'customers.read', 'customers.write', 'visits.read', 'visits.write', 'photos.write', 'reports.read', 'messages.write', 'payments.record'],
  Technician: ['schedule.read.assigned', 'customers.read.assigned', 'visits.read.assigned', 'visits.write.assigned', 'photos.write.assigned'],
  'Commission agent': ['leads.read.credited', 'customers.read.credited', 'commission.read.own'],
  Office: ['money.read', 'messages.read', 'messages.write'],
  'Read-only': ['*.read'],
};

export function effectiveAppPermissions(roles = []) {
  const permissions = new Set();
  (Array.isArray(roles) ? roles : []).forEach((role) => (ROLE_PERMISSIONS[role] || []).forEach((permission) => permissions.add(permission)));
  return permissions;
}

export function hasAppPermission(roles, permission) {
  const permissions = effectiveAppPermissions(roles);
  if (permissions.has('*') || permissions.has(permission)) return true;
  // Read-only reads everything except what is reserved for an Owner (profit and totals, whole-list exports, and the like).
  return permissions.has('*.read') && /(^|\.)read($|\.)/.test(permission) && !managerOwnerOnlyPermissions().includes(permission);
}

export async function changeMembershipRoles(client, { membershipId, appKey, actorRoles, roles }) {
  const guard = decideOwnerAction(actorRoles, 'roles.change');
  if (!guard.allowed) return { error: new Error(guard.reason) };
  const valid = [...new Set(roles || [])];
  if (!valid.length || valid.some((role) => !APP_ROLE_NAMES.includes(role))) return { error: new Error('Choose one or more of the six available roles.') };
  const result = await client.from('app_memberships').update({ roles: valid }).eq('id', membershipId).eq('app_key', appKey).select('id, user_id, app_key, roles, is_active').single();
  return { membership: result.data || null, error: result.error || null };
}

export function normalizeAppMembership(membership) {
  if (!membership || !membership.is_active) return null;
  return { ...membership, roles: [...new Set((membership.roles || []).filter((role) => APP_ROLE_NAMES.includes(role)))] };
}

export function canManageAppRoles(actorRoles = []) {
  return actorRoles.includes('Owner');
}

export function canManageCommission(actorRoles = []) {
  return actorRoles.includes('Owner') || actorRoles.includes('Manager');
}

export function managerOwnerOnlyPermissions() {
  return ['prices.change', 'invoices.edit', 'financial_totals.read', 'customers.export', 'payment_details.change', 'templates.change', 'people.add', 'roles.change', 'customers.delete.approve', 'visits.delete.approve'];
}

export function appsAvailableToUser(memberships = []) {
  return [...new Set((memberships || []).map(normalizeAppMembership).filter((m) => m && m.roles.length).map((m) => m.app_key))];
}

// PROVISIONAL scope vocabulary (mirrors public.genserv_member_scope in supabase/migrations/20261029_wo2929_genserv_role_policies.sql):
//   legacy   = no membership row for the app at all: behaviour is exactly what it was before WO-2929 (all six existing Charlie users stay here until an Owner grants memberships)
//   none     = membership rows exist but none is active: access has ended
//   all      = holds Owner, Manager or Read-only: sees every GenServ row
//   assigned = holds only Technician, Commission agent or Office: sees only customers/visits assigned or credited to them
export const BROAD_SCOPE_ROLES = ['Owner', 'Manager', 'Read-only'];

export function appAccess(memberships = [], appKey) {
  const rows = (memberships || []).filter((m) => m && m.app_key === appKey);
  if (!rows.length) return { scope: 'legacy', roles: [] };
  const roles = [...new Set(rows.map(normalizeAppMembership).filter(Boolean).flatMap((m) => m.roles))];
  if (!roles.length) return { scope: 'none', roles: [] };
  return { scope: roles.some((r) => BROAD_SCOPE_ROLES.includes(r)) ? 'all' : 'assigned', roles };
}

// Reference model of the row rules the restrictive policies enforce, used by the tests and the six-user comparison.
export function canReadCustomer(access, { contactId, userId, jobs = [], assignments = [] }) {
  if (access.scope === 'legacy' || access.scope === 'all') return true;
  if (access.scope === 'none') return false;
  return jobs.some((j) => j.contact_id === contactId && j.assigned_to === userId) || assignments.some((a) => a.contact_id === contactId && a.user_id === userId && a.is_active);
}

export function canReadVisit(access, { job, userId }) {
  if (access.scope === 'legacy' || access.scope === 'all') return true;
  return access.scope === 'assigned' && job.assigned_to === userId;
}

export function canWriteRow(access, permission, { row = {}, userId } = {}) {
  if (access.scope === 'legacy') return true;
  if (access.scope === 'none') return false;
  if (hasAppPermission(access.roles, permission)) return true;
  return row.assigned_to === userId && hasAppPermission(access.roles, `${permission}.assigned`);
}

const OWNER_ACTION_LABELS = {
  'prices.change': 'change prices',
  'invoices.edit': 'edit invoices',
  'financial_totals.read': 'see profit and financial totals',
  'customers.export': 'export the customer list',
  'payment_details.change': 'change payment details',
  'templates.change': 'change templates',
  'people.add': 'add people',
  'roles.change': 'change app roles',
  'customers.delete.approve': 'delete customers',
  'visits.delete.approve': 'delete visits',
};

export function ownerOnlyActionLabel(action) { return OWNER_ACTION_LABELS[action] || action; }

// One guard for every Owner-only action, used by the screens. `legacy` people (no membership yet) are not changed by this WO.
export function decideOwnerAction(actorRoles = [], action, scope = 'all') {
  if (scope === 'legacy' || canManageAppRoles(actorRoles)) return { allowed: true, reason: null };
  return { allowed: false, reason: `Only an Owner can ${ownerOnlyActionLabel(action)}.` };
}

// Screen guard for an Owner-only action, from the person's resolved access ({ scope, roles } from appAccess). Unknown access (null, still loading or unreadable) behaves as legacy
// (the unchanged pre-WO-2929 behaviour); a Manager or anyone else holding a membership is refused unless they also hold Owner.
export function ownerActionVerdict(access, action) {
  const a = access || { scope: 'legacy', roles: [] };
  return decideOwnerAction(a.roles, action, a.scope);
}

// The database half of the customer export refusal (public.genserv_assert_export_allowed raises 42501 for a non-Owner). A missing function (migration not applied yet) or any
// other failure leaves the screen guard as the only check, exactly as before this WO.
export async function assertCustomerExportAllowed(client) {
  try {
    const { error } = await client.rpc('genserv_assert_export_allowed');
    if (error && (error.code === '42501' || /only an owner/i.test(error.message || ''))) return { allowed: false, reason: 'Only an Owner can export the customer list.' };
  } catch { /* no rpc available: screen guard only */ }
  return { allowed: true, reason: null };
}

async function activeOwnerCount(client, appKey) {
  const { data, error } = await client.from('app_memberships').select('id, roles').eq('app_key', appKey).eq('is_active', true);
  if (error) return { error };
  return { count: (data || []).filter((m) => (m.roles || []).includes('Owner')).length };
}

export async function addMembership(client, { actorRoles, actorId, userId, appKey, roles }) {
  const guard = decideOwnerAction(actorRoles, 'people.add');
  if (!guard.allowed) return { error: new Error(guard.reason) };
  const valid = [...new Set(roles || [])];
  if (!valid.length || valid.some((role) => !APP_ROLE_NAMES.includes(role))) return { error: new Error('Choose one or more of the six available roles.') };
  const result = await client.from('app_memberships').insert({ user_id: userId, app_key: appKey, roles: valid, granted_by: actorId }).select('id, user_id, app_key, roles, is_active').single();
  return { membership: result.data || null, error: result.error || null };
}

// Ending a membership only switches it off: no customer, visit or lead row is touched (AC-07).
export async function deactivateMembership(client, { actorRoles, membership }) {
  const guard = decideOwnerAction(actorRoles, 'people.add');
  if (!guard.allowed) return { error: new Error(guard.reason) };
  if ((membership.roles || []).includes('Owner')) {
    const { count, error } = await activeOwnerCount(client, membership.app_key);
    if (error) return { error };
    if (count <= 1) return { error: new Error('The last Owner cannot be removed.') };
  }
  const result = await client.from('app_memberships').update({ is_active: false }).eq('id', membership.id).select('id, is_active').single();
  return { membership: result.data || null, error: result.error || null };
}

// Which screens a person may open, from their memberships. Unknown (null) or no rows at all = the unchanged pre-WO-2929 behaviour.
export function routeVerdict(memberships, activeKey, isGenservKey) {
  if (!Array.isArray(memberships) || !memberships.length) return { allowed: true, reason: null, home: null };
  const genserv = isGenservKey(activeKey);
  const apps = appsAvailableToUser(memberships);
  const needed = genserv ? 'genserv' : 'charlie';
  const hasRowForNeeded = memberships.some((m) => m && m.app_key === needed);
  // An app the person has no membership row for stays on its old rules only if they have no rows anywhere; once they hold GenServ-only rows Charlie is closed to them.
  const closed = apps.includes(needed) ? false : (hasRowForNeeded || (!genserv && apps.length > 0));
  if (!closed) return { allowed: true, reason: null, home: null };
  return { allowed: false, reason: genserv ? 'Your GenServ access has ended.' : 'This area is not part of your access.', home: apps.includes('genserv') ? 'genserv' : null };
}
